import React, { useState } from "react";
import API_BASE from "../config";
import { TRANSLATE_LANGUAGES as LANGUAGES } from "../lib/translateSettings";
import { rememberSuppressedRecall } from "../lib/translateRecall";

/**
 * Non-blocking call-ended prompt that offers to remember a translation
 * language for a number:
 *   - unsaved number : "Save <number> as a contact with <Lang>?"  [Save][Not now]
 *   - saved, no lang : "Always translate calls with <name> in <Lang>?"  [Always][Not now]
 *   - saved, differs : "Keep <Lang>?"  [Keep] [Use <usedLang>]
 * Tapping Not now / Keep records a 30-day per-number suppression. The prompt
 * performs its own PUT/POST against /contacts and reloads the line's contacts.
 */
export default function TranslateRecallPrompt({ recall, authToken, selectedNumber, onReload, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!recall) return null;

  const { kind, number, lang, usedLang, contactId, name } = recall;
  const lineQs = `?line=${encodeURIComponent(selectedNumber)}`;
  const langName = (code) => (LANGUAGES[code] ? LANGUAGES[code].name : code);
  const displayName = name || number;

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
    "ngrok-skip-browser-warning": "true",
  });

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  const saveAsContact = async () => {
    const res = await fetch(`${API_BASE}/contacts${lineQs}`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ number, first_name: "", last_name: "", translate_lang: lang }),
    });
    if (res.ok) onReload();
  };

  const setContactLang = async (code, id) => {
    if (!id) return;
    const res = await fetch(`${API_BASE}/contacts/${encodeURIComponent(id)}${lineQs}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ translate_lang: code }),
    });
    if (res.ok) onReload();
  };

  let message;
  let buttons = [];
  if (kind === "save") {
    message = `Save ${number} as a contact with ${langName(lang)}?`;
    buttons = [
      { label: "Save", primary: true, onClick: () => run(saveAsContact) },
      { label: "Not now", primary: false, onClick: () => run(() => rememberSuppressedRecall(number)) },
    ];
  } else if (kind === "always") {
    message = `Always translate calls with ${displayName} in ${langName(lang)}?`;
    buttons = [
      { label: "Always", primary: true, onClick: () => run(() => setContactLang(lang, contactId)) },
      { label: "Not now", primary: false, onClick: () => run(() => rememberSuppressedRecall(number)) },
    ];
  } else if (kind === "keep") {
    message = `Keep ${langName(lang)}?`;
    buttons = [
      { label: "Keep", primary: false, onClick: () => run(() => rememberSuppressedRecall(number)) },
      {
        label: `Use ${langName(usedLang)}`,
        primary: true,
        onClick: () => run(() => setContactLang(usedLang, contactId)),
      },
    ];
  } else {
    return null;
  }

  const baseBtn = {
    marginLeft: 8,
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #4aa3ff",
    background: "transparent",
    color: "#4aa3ff",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  return (
    <div
      role="status"
      data-testid="translate-recall-prompt"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        margin: "10px 0",
        padding: "10px 14px",
        borderRadius: 10,
        background: "rgba(22,22,35,0.9)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        fontSize: 15,
      }}
    >
      <span>{message}</span>
      <span style={{ display: "flex", flexShrink: 0 }}>
        {buttons.map((b) => (
          <button
            key={b.label}
            type="button"
            disabled={busy}
            onClick={b.onClick}
            style={b.primary
              ? { ...baseBtn, background: "linear-gradient(135deg,#2e9bff,#1f6feb)", color: "#fff" }
              : baseBtn}
          >
            {b.label}
          </button>
        ))}
      </span>
    </div>
  );
}
